import { ArrowRight, Loader2, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SettingsSection } from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { dispatchOpenPlatformSkill } from '@/lib/open-platform-skill'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type SkillTier = 'workspace' | 'user' | 'platform'

interface SkillListItem {
  id: string
  name: string
  description?: string
  category?: string
  triggerKeywords?: string[]
  tier: SkillTier
  source?: 'database' | 'filesystem'
  documentId?: string | null
  filePath?: string | null
  /** Org slugs this skill is restricted to. Workspace skills use their owning org. Empty means universal (platform skills). */
  requiredWorkspaces?: string[]
}

const TIER_LABELS: Record<SkillTier, string> = {
  workspace: 'Workspace',
  user: 'Personal',
  platform: 'Platform',
}

const TIER_COLORS: Record<SkillTier, string> = {
  workspace: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  user: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  platform: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
}

/** Cheap client-side fallback when we don't have a workspace-name lookup handy: "hop-studios" -> "Hop Studios" */
function humanizeWorkspaceSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Tier badge text, e.g. "Platform" (universal) vs "Platform - Hop Studios" (scoped, per requiredWorkspaces). */
function tierBadgeText(skill: SkillListItem): string {
  const base = TIER_LABELS[skill.tier] || skill.tier
  const scopes = skill.requiredWorkspaces?.filter(Boolean) || []
  if (scopes.length === 0) return base
  return `${base} - ${scopes.map(humanizeWorkspaceSlug).join(', ')}`
}

interface ChatSkillsSettingsProps {
  /** Closes the Settings panel - used when a row opens the main panel. */
  onClose?: () => void
}

/**
 * Read-mostly registry of chat skills (platform / workspace / personal).
 * Rows close Settings and open in the main panel - no nested preview modal.
 * Bet: docs/product/bets/2026-07-04-unify-chat-skills-as-documents.md
 */
export function ChatSkillsSettings({ onClose }: ChatSkillsSettingsProps) {
  const [skills, setSkills] = useState<SkillListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetchWithAuth('/api/skills')
        const data = await res.json()
        if (cancelled) return
        if (!data.success) {
          setError(data.error || 'Failed to load skills')
          return
        }
        setSkills(Array.isArray(data.skills) ? data.skills : [])
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const openSkillInMaater = (skill: SkillListItem) => {
    if (skill.documentId) {
      window.dispatchEvent(
        new CustomEvent('open-document', { detail: { documentId: skill.documentId } }),
      )
      onClose?.()
      return
    }
    if (skill.filePath) {
      window.dispatchEvent(
        new CustomEvent('open-document', { detail: { filePath: skill.filePath } }),
      )
      onClose?.()
      return
    }
    if (skill.tier === 'platform') {
      dispatchOpenPlatformSkill({
        slug: skill.id,
        name: skill.name,
        category: skill.category,
      })
      onClose?.()
    }
  }

  const openChatToCreateSkill = () => {
    window.dispatchEvent(
      new CustomEvent('flownotes:open-chat-prompt', {
        detail: { query: '/create-skill', newThread: false },
      }),
    )
    onClose?.()
  }

  return (
    <div className="space-y-6" data-testid={testIds.settings.chatSkills}>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Skills in the / menu. Click a row to open it in Maater: workspace and personal skills
        edit as documents; platform skills are git-managed and read-only.
      </p>

      <div className="rounded-lg border border-border bg-muted/10 p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
          <span>
            To create a skill, run{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">/create-skill</code> in chat.
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          data-testid={testIds.settings.chatSkillsCreateLink}
          onClick={openChatToCreateSkill}
        >
          Open chat
          <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Loading skills...
        </div>
      ) : (
        <SettingsSection title="All skills" data-testid={testIds.settings.chatSkillsList}>
          {skills.length === 0 && !error ? (
            <p className="text-sm text-muted-foreground py-2">No skills found.</p>
          ) : null}
          <div className="space-y-2">
            {skills.map((skill) => {
              const canOpen =
                skill.tier === 'platform' || Boolean(skill.documentId || skill.filePath)
              const actionLabel =
                skill.documentId || skill.filePath ? 'Edit in Maater' : 'View in Maater'
              return (
                <button
                  key={`${skill.tier}-${skill.id}`}
                  type="button"
                  data-testid={testIds.settings.chatSkillsRow(skill.id)}
                  disabled={!canOpen}
                  onClick={() => canOpen && openSkillInMaater(skill)}
                  className={cn(
                    'w-full min-w-0 rounded-md border border-border bg-background/60 px-3.5 py-3.5 text-left transition-colors',
                    canOpen
                      ? 'hover:bg-muted/40 cursor-pointer'
                      : 'cursor-default opacity-90',
                  )}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{skill.name}</span>
                    <Badge className={cn('border text-xs', TIER_COLORS[skill.tier] || TIER_COLORS.workspace)}>
                      {tierBadgeText(skill)}
                    </Badge>
                    {skill.category ? (
                      <span className="text-xs text-muted-foreground">{skill.category}</span>
                    ) : null}
                    {canOpen ? (
                      <span className="ml-auto text-xs italic text-muted-foreground">
                        {actionLabel}
                      </span>
                    ) : null}
                  </div>
                  {skill.description ? (
                    <p className="text-sm leading-relaxed text-muted-foreground mt-1 break-words">
                      {skill.description}
                    </p>
                  ) : null}
                  {skill.triggerKeywords && skill.triggerKeywords.length > 0 ? (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Triggers: {skill.triggerKeywords.slice(0, 4).join(', ')}
                      {skill.triggerKeywords.length > 4 ? '...' : ''}
                    </p>
                  ) : null}
                </button>
              )
            })}
          </div>
        </SettingsSection>
      )}
    </div>
  )
}
